import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { DollarIcon, DeleteIcon, PlusIcon, ResetIcon } from '@/components/Icons'
import { registrationOptions } from '@/constants/event'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { HelpCircle } from 'lucide-react'
import CustomRichTextEditor from '../CustomRichTextEditor'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import {
  handleIntegerKeyDown,
  handlePriceInput,
  handlePriceKeyDown,
  handlePriceClick,
  formatPriceWithCommas,
} from '@/utils/formFieldUtils'
import { ErrorMessage } from '../ErrorMessage'
import { MAX_TICKETS_LIMIT } from '@/constants'

interface EventRegistrationFormProps {
  totalTickets?: number
}

export default function EventRegistrationForm({
  totalTickets = 0,
}: Readonly<EventRegistrationFormProps>) {
  const { control, watch, setValue, clearErrors } =
    useFormContext<EventFormDataType>()
  const registrationType = watch('registrationType')

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'eventTicket',
  })

  const existingTicketsCount = fields.filter(field => !field.isNew).length
  const maxAllowedTickets =
    MAX_TICKETS_LIMIT - totalTickets + existingTicketsCount

  const handleAddTicket = () => {
    if (fields.length >= maxAllowedTickets) return
    append({
      id: Date.now().toString(),
      name: '',
      price: null,
      description: '',
      perks: '',
      maxAttendeesLimit: '',
      isNew: true,
      isSubEventTicket: false,
    })
  }

  const clearTicketErrors = (index: number) => {
    clearErrors([
      `eventTicket.${index}.name`,
      `eventTicket.${index}.price`,
      `eventTicket.${index}.description`,
      `eventTicket.${index}.perks`,
      `eventTicket.${index}.maxAttendeesLimit`,
      `eventTicket.${index}.isSubEventTicket`,
    ])
  }

  const handleRemoveTicket = (index: number) => {
    if (fields.length <= 1) return
    clearTicketErrors(index)
    remove(index)
  }

  const handleTicketReset = (index: number) => {
    setValue(`eventTicket.${index}.name`, '')
    setValue(`eventTicket.${index}.price`, '')
    setValue(`eventTicket.${index}.description`, '')
    setValue(`eventTicket.${index}.perks`, '')
    setValue(`eventTicket.${index}.maxAttendeesLimit`, '')
    setValue(`eventTicket.${index}.isSubEventTicket`, false)
    clearTicketErrors(index)
  }

  return (
    <>
      <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <DollarIcon className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950 flex gap-1 items-center">
          Pricing & Registration
          {registrationType === 'paid' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-4 w-4 text-ss-neutral-600"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                Maximum {MAX_TICKETS_LIMIT} tickets can be created combining
                main and sub event tickets
              </TooltipContent>
            </Tooltip>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Registration Type */}
        <section className="space-y-2.5 max-w-[541px]">
          <Label className="text-sm font-semibold text-ss-neutral-950">
            Registration Type
          </Label>
          <Controller
            name="registrationType"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="space-y-3 mb-4 flex gap-4"
                aria-label="Registration type options"
              >
                {registrationOptions.map(option => (
                  <div key={option.id} className="relative mb-0 gap-3">
                    <Label
                      htmlFor={option.id}
                      className={cn(
                        'flex items-center gap-2 rounded-lg cursor-pointer transition-all duration-200',
                        field.value === option.id
                          ? 'border-ss-blue-500 bg-white'
                          : 'border-border bg-background'
                      )}
                    >
                      <RadioGroupItem
                        value={option.id}
                        id={option.id}
                        className={cn(
                          field.value === option.id
                            ? 'border-ss-blue-500 bg-ss-blue-500 text-white'
                            : 'border-border bg-background'
                        )}
                        aria-label={option.title}
                      />
                      <span className="font-normal text-ss-neutral-950 text-base leading-[22px]">
                        {option.title}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </section>

        {registrationType === registrationOptions[1].id && (
          <section className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="space-y-4">
                {/* Ticket Header */}
                <div className="bg-ss-neutral-25 box-border flex flex-col gap-2.5 items-start justify-start px-3 py-1.5 relative rounded border-r-[#cdd1d9] w-full">
                  <div className="flex gap-8 items-start justify-start w-full">
                    <div className="flex gap-1.5 grow items-center justify-between">
                      <h3 className="font-bold text-ss-neutral-950 text-base leading-[22px]">
                        Ticket {index + 1}
                      </h3>
                      <div className="flex align-middle items-center">
                        {fields.length === 1 &&
                          (() => {
                            const ticket = watch('eventTicket')?.[0] || {}
                            const hasAnyValue = !!(
                              ticket.name ||
                              ticket.price ||
                              ticket.description ||
                              ticket.perks ||
                              ticket.maxAttendeesLimit ||
                              ticket.isSubEventTicket
                            )
                            const isBooked = ticket.isBooked || false
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      disabled={
                                        fields.length >= maxAllowedTickets ||
                                        !hasAnyValue ||
                                        isBooked
                                      }
                                      type="button"
                                      variant="ghost"
                                      aria-label="Reset ticket fields"
                                      className="p-0 flex text-ss-blue-900 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-900 hover:bg-transparent"
                                      onClick={() => {
                                        if (fields.length === 1) {
                                          handleTicketReset(index)
                                        }
                                      }}
                                    >
                                      <ResetIcon
                                        className="size-5 hover:text-ss-blue-900"
                                        aria-hidden="true"
                                      />
                                      <span className="font-medium  text-sm leading-4.5   hover:text-ss-blue-900 disabled:text-ss-neutral-200">
                                        Reset
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isBooked && (
                                  <TooltipContent>
                                    Someone has already booked this ticket.
                                    Can't reset.
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )
                          })()}
                        {index === fields.length - 1 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  disabled={fields.length >= maxAllowedTickets}
                                  type="button"
                                  variant="ghost"
                                  onClick={handleAddTicket}
                                  aria-label="Add more tickets"
                                  className="p-0 flex text-ss-blue-600 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-700 hover:bg-transparent"
                                >
                                  <PlusIcon
                                    className="size-5 hover:text-ss-blue-700"
                                    aria-hidden="true"
                                  />
                                  <span className="font-medium  text-sm leading-4.5   hover:text-ss-blue-700 disabled:text-ss-neutral-200">
                                    Add More
                                  </span>
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {fields.length >= maxAllowedTickets && (
                              <TooltipContent>
                                Maximum {MAX_TICKETS_LIMIT} tickets can be
                                created combining main and sub event tickets
                              </TooltipContent>
                            )}
                          </Tooltip>
                        )}
                        {fields.length > 1 &&
                          (() => {
                            const ticket = watch('eventTicket')?.[index] || {}
                            const isBooked = ticket.isBooked || false
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      disabled={isBooked}
                                      type="button"
                                      variant="ghost"
                                      onClick={() => handleRemoveTicket(index)}
                                      className="gap-1 flex p-0 h-auto disabled:opacity-50 disabled:bg-transparent disabled:cursor-not-allowed"
                                      aria-label={`Delete Ticket ${index + 1}`}
                                    >
                                      <DeleteIcon
                                        strokeWidth={1.4}
                                        className="h-5 w-5 text-ss-critical-200"
                                        aria-hidden="true"
                                      />
                                      <span className="font-medium text-ss-critical-200 text-sm leading-4.5 ">
                                        Delete
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isBooked && (
                                  <TooltipContent>
                                    Someone has already booked this ticket.
                                    Can't delete.
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )
                          })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ticket Form Fields */}
                <div className="space-y-4">
                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex flex-col flex-1">
                      <Label
                        variant="required"
                        htmlFor={`ticket-name-${index}`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Ticket Name
                      </Label>
                      <Controller
                        name={`eventTicket.${index}.name`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              id={`ticket-name-${index}`}
                              required
                              type="text"
                              placeholder="Enter ticket name"
                              className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <Label
                        variant="required"
                        htmlFor={`ticket-price-${index}`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Ticket Price
                      </Label>
                      <Controller
                        name={`eventTicket.${index}.price`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <div className="relative">
                              <Input
                                id={`ticket-price-${index}`}
                                type="text"
                                inputMode="decimal"
                                value={formatPriceWithCommas(
                                  field.value?.toString() || ''
                                )}
                                onChange={e =>
                                  handlePriceInput(e, field.onChange)
                                }
                                onKeyDown={handlePriceKeyDown}
                                onClick={handlePriceClick}
                                onFocus={handlePriceClick}
                                placeholder="Enter ticket price"
                                className={cn(
                                  'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20 pl-8',
                                  error && 'border-ss-critical-200'
                                )}
                                aria-invalid={!!error}
                              />
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ss-neutral-600 text-base">
                                $
                              </span>
                            </div>
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* Description and What's Included */}
                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor={`ticket-description-${index}`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Description
                      </Label>
                      <Controller
                        name={`eventTicket.${index}.description`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Textarea
                              {...field}
                              id={`ticket-description-${index}`}
                              required
                              placeholder="Add ticket description"
                              className="h-[137px] resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor={`ticket-included-${index}`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        What's Included
                      </Label>
                      <Controller
                        name={`eventTicket.${index}.perks`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <CustomRichTextEditor
                              toolbarFeatures={['bulletList']}
                              value={field.value ?? ''}
                              onChange={value =>
                                field.onChange(value === '<p></p>' ? '' : value)
                              }
                              height={'85px'}
                              placeholder="Add ticket specification"
                              className="resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* Per-ticket Max Attendees */}
                  <div className="max-w-[539px]">
                    <Label
                      variant="required"
                      htmlFor={`ticket-maxAttendees-${index}`}
                      className="text-sm font-semibold text-ss-neutral-950 leading-4.5  mb-1 block"
                    >
                      Max Attendees for this Ticket
                    </Label>
                    <Controller
                      name={`eventTicket.${index}.maxAttendeesLimit`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <Input
                            {...field}
                            value={field.value?.toString() || ''}
                            onChange={e => field.onChange(e.target.value || '')}
                            onKeyDown={handleIntegerKeyDown}
                            id={`ticket-maxAttendees-${index}`}
                            type="number"
                            placeholder="Enter max attendees for this ticket"
                            min={1}
                            className={cn(
                              'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                              error && 'border-ss-critical-200'
                            )}
                            required
                            aria-invalid={!!error}
                          />
                          {error && <ErrorMessage error={error.message} />}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {registrationType === registrationOptions[0].id && (
          <>
            <div className="flex gap-4 w-full sm:flex-row flex-col">
              <div className="flex flex-col flex-1">
                <Label
                  variant="required"
                  htmlFor={`ticketName`}
                  className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                >
                  Ticket Name
                </Label>
                <Controller
                  name={`ticketName`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value?.toString() || ''}
                        id={`ticketName`}
                        required
                        type="text"
                        placeholder="Enter ticket name"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && <ErrorMessage error={error.message} />}
                    </>
                  )}
                />
              </div>
            </div>
            <div className="flex gap-4 w-full sm:flex-row flex-col">
              <div className="flex-1 flex flex-col min-h-px min-w-px">
                <Label
                  variant="required"
                  htmlFor={`ticketDescription`}
                  className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                >
                  Description
                </Label>
                <Controller
                  name={`ticketDescription`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Textarea
                        {...field}
                        value={field.value?.toString() || ''}
                        id={`ticketDescription`}
                        required
                        placeholder="Add ticket description"
                        className="h-[137px] resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500"
                      />
                      {error && <ErrorMessage error={error.message} />}
                    </>
                  )}
                />
              </div>
              <div className="flex-1 flex flex-col min-h-px min-w-px">
                <Label
                  variant="required"
                  htmlFor={`ticketPerks`}
                  className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                >
                  What's Included
                </Label>
                <Controller
                  name={`ticketPerks`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <CustomRichTextEditor
                        toolbarFeatures={['bulletList']}
                        value={field.value ?? ''}
                        onChange={value =>
                          field.onChange(value === '<p></p>' ? '' : value)
                        }
                        height={'85px'}
                        placeholder="Add ticket specification"
                        className="resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && <ErrorMessage error={error.message} />}
                    </>
                  )}
                />
              </div>
            </div>
            <div className="max-w-[539px]">
              <Label
                variant="required"
                htmlFor="maxAttendeesLimit"
                className="text-sm font-semibold text-ss-neutral-950 leading-4.5  mb-1 block"
              >
                Max Attendees Limit
              </Label>
              <Controller
                name="maxAttendees"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Input
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={e =>
                        field.onChange(Number(e.target.value) || '')
                      }
                      onKeyDown={handleIntegerKeyDown}
                      id="maxAttendeesLimit"
                      type="number"
                      placeholder="Enter maxAttendees Limit"
                      min={1}
                      className={cn(
                        'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                        error && 'border-ss-critical-200'
                      )}
                      aria-invalid={!!error}
                    />
                    {error && <ErrorMessage error={error.message} />}
                  </>
                )}
              />
            </div>
          </>
        )}
      </CardContent>
    </>
  )
}
